import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CreditCard, Wallet, Smartphone, Gift, Store, Settings, CheckCircle2, XCircle, ArrowLeftRight, Loader2 } from 'lucide-react';

export default function Payments() {
  const { token } = useAuth();
  const { currentLocationId } = useTenantLocation();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [journalId, setJournalId] = useState('');
  const [journalName, setJournalName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchMethods = async () => {
    setLoading(true);
    try {
      const response = await api.getPaymentMethods(token);
      setMethods(response.data);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentLocationId]);

  const handleUpdateMapping = async () => {
    if (!selectedMethod || !journalId || !journalName) return;
    setUpdating(true);
    try {
      await api.updatePaymentMethod(token, selectedMethod.id, {
        odoo_journal_id: journalId,
        odoo_journal_name: journalName
      });
      toast.success('Payment mapping updated');
      setDialogOpen(false);
      fetchMethods();
    } catch (error) {
      toast.error('Failed to update mapping');
    } finally {
      setUpdating(false);
    }
  };

  const openEditDialog = (method) => {
    setSelectedMethod(method);
    setJournalId(method.odoo_journal_id || '');
    setJournalName(method.odoo_journal_name || '');
    setDialogOpen(true);
  };

  const getMethodIcon = (name) => {
    const icons = {
      'Cash': Wallet,
      'Credit Card': CreditCard,
      'Debit Card': CreditCard,
      'Apple Pay': Smartphone,
      'Google Pay': Smartphone,
      'Gift Card': Gift,
      'Store Credit': Store
    };
    const Icon = icons[name] || CreditCard;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Payment Methods
          </h1>
          <p className="text-slate-400 mt-1">Map UnTill payment methods to Odoo journals</p>
        </div>
      </div>

      {/* Mapping Info */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-8 h-8 text-blue-400" />
              </div>
              <p className="text-white font-medium">UnTill Payment</p>
              <p className="text-xs text-slate-500">Methods</p>
            </div>
            <ArrowLeftRight className="w-8 h-8 text-slate-500" />
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
                <Store className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-white font-medium">Odoo Journal</p>
              <p className="text-xs text-slate-500">Accounts</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{methods.length}</p>
            <p className="text-slate-400 text-sm">Total Methods</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{methods.filter(m => m.odoo_journal_id).length}</p>
            <p className="text-slate-400 text-sm">Mapped</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{methods.filter(m => !m.odoo_journal_id).length}</p>
            <p className="text-slate-400 text-sm">Unmapped</p>
          </CardContent>
        </Card>
      </div>

      {/* Methods Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">Payment Method Mapping</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : methods.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No payment methods found. Seed demo data from the dashboard.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Payment Method</TableHead>
                  <TableHead className="text-slate-400">UnTill Code</TableHead>
                  <TableHead className="text-slate-400">Odoo Journal</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Split Payment</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {methods.map((method) => (
                  <TableRow key={method.id} className="border-slate-800 data-table-row">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400">
                          {getMethodIcon(method.name)}
                        </div>
                        <span className="text-white font-medium">{method.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-slate-400">{method.utill_code}</TableCell>
                    <TableCell>
                      {method.odoo_journal_name ? (
                        <div>
                          <p className="text-white">{method.odoo_journal_name}</p>
                          <p className="text-slate-500 text-xs">ID: {method.odoo_journal_id}</p>
                        </div>
                      ) : (
                        <span className="text-amber-400 text-sm">Not Mapped</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {method.is_active ? (
                        <Badge className="status-success">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge className="status-error">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {method.supports_split ? (
                        <Badge variant="outline" className="border-slate-700 text-slate-300">Supported</Badge>
                      ) : (
                        <span className="text-slate-500 text-sm">No</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(method)}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        data-testid={`edit-mapping-${method.utill_code}`}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Configure
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Configure Payment Mapping</DialogTitle>
            <DialogDescription className="text-slate-400">
              Map {selectedMethod?.name} to an Odoo journal account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Odoo Journal ID</Label>
              <Input
                value={journalId}
                onChange={(e) => setJournalId(e.target.value)}
                placeholder="e.g., 1"
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="journal-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Odoo Journal Name</Label>
              <Input
                value={journalName}
                onChange={(e) => setJournalName(e.target.value)}
                placeholder="e.g., Cash Journal"
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="journal-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateMapping}
              disabled={updating}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="save-mapping-btn"
            >
              {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
